import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, ShieldCheck, Mail, RefreshCw, Search, Send, Loader2, CheckCircle2, AlertTriangle, Bot, Download, Truck, PackageCheck, Zap } from 'lucide-react';

import { API_BASE } from "../utils/apiBase";

const API = API_BASE;

// Defensive JSON parse — some prod CDNs/SWs cause `r.json()` to throw
// "body stream already read". Reading as text first sidesteps that and
// also lets us surface non-JSON responses (HTML 404 pages etc.) cleanly.
const safeJson = async (response) => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (_err) {
    throw new Error(`Non-JSON response (${response.status}): ${text.slice(0, 120)}`);
  }
};

const AdminPage = () => {
  const [busy, setBusy] = useState(null);
  const [logs, setLogs] = useState([]);
  const [orderId, setOrderId] = useState('');
  const [emailQ, setEmailQ] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Bracelet orders state (legacy — kept for old orders)
  const [bracelets, setBracelets] = useState(null);
  const [braceletLoading, setBraceletLoading] = useState(false);
  const [braceletStatusFilter, setBraceletStatusFilter] = useState('');

  // Consultation orders state (₹299 chat with Astro Avinash)
  const [consults, setConsults] = useState(null);
  const [consultLoading, setConsultLoading] = useState(false);
  const [consultStatusFilter, setConsultStatusFilter] = useState('');

  // Drip / Email tracking state
  const [dripStats, setDripStats] = useState(null);
  const [recentSends, setRecentSends] = useState(null);
  const [emailHistoryQ, setEmailHistoryQ] = useState('');
  const [emailHistory, setEmailHistory] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // Autoreply state
  const [autoreplies, setAutoreplies] = useState(null);
  const [autoreplyLoading, setAutoreplyLoading] = useState(false);

  const log = (line, kind = 'info') => {
    setLogs((prev) => [{ kind, t: new Date().toLocaleTimeString(), line }, ...prev].slice(0, 50));
  };

  const call = async (action, fn) => {
    setBusy(action);
    try {
      const data = await fn();
      log(`${action} → ${JSON.stringify(data)}`, 'ok');
    } catch (e) {
      log(`${action} ✗ ${e?.response?.data ? JSON.stringify(e.response.data) : e.message}`, 'err');
    } finally {
      setBusy(null);
    }
  };

  const runWatchdog = () =>
    call('Run Watchdog', async () => {
      const r = await fetch(`${API}/api/admin/run-watchdog`, { method: 'POST' });
      return await r.json();
    });

  const checkOrder = () =>
    call(`Order Status (${orderId.slice(0, 8)})`, async () => {
      if (!orderId.trim()) throw new Error('Enter order id');
      const r = await fetch(`${API}/api/admin/order-status/${orderId.trim()}`);
      return await r.json();
    });

  const resendOrder = () =>
    call(`Resend Order (${orderId.slice(0, 8)})`, async () => {
      if (!orderId.trim()) throw new Error('Enter order id');
      const r = await fetch(`${API}/api/admin/send-email/${orderId.trim()}`, { method: 'POST' });
      return await r.json();
    });

  const redeliverByEmail = () =>
    call(`Redeliver to ${emailQ}`, async () => {
      if (!emailQ.trim()) throw new Error('Enter email');
      const r = await fetch(`${API}/api/admin/redeliver-by-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailQ.trim() }),
      });
      return await r.json();
    });

  const testEmailSend = () =>
    call(`Test Email to ${testEmail}`, async () => {
      if (!testEmail.trim()) throw new Error('Enter email');
      const r = await fetch(`${API}/api/admin/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: testEmail.trim() }),
      });
      return await r.json();
    });

  const redeliverAllPaid = (force) =>
    call(`Redeliver ALL Paid${force ? ' (force)' : ''}`, async () => {
      const r = await fetch(`${API}/api/admin/redeliver-all-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: !!force }),
      });
      return await r.json();
    });

  const redeliverStuck = () =>
    call('Auto-Resend Stuck (>24h)', async () => {
      const r = await fetch(`${API}/api/admin/redeliver-stuck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours_threshold: 24 }),
      });
      const data = await r.json();
      // Refresh the dashboard immediately so the user sees the in-progress
      // counter tick up.
      fetchPending();
      return data;
    });

  const forceDeliverAllNow = () =>
    call('Force Deliver All Now', async () => {
      const r = await fetch(`${API}/api/admin/force-deliver-now`, { method: 'POST' });
      const data = await safeJson(r);
      await fetchPending();
      return data;
    });

  const reconcileNow = () =>
    call('Reconcile Razorpay Now', async () => {
      const r = await fetch(`${API}/api/admin/reconcile-now`, { method: 'POST' });
      const data = await safeJson(r);
      await fetchPending();
      return data;
    });

  const fetchEmailStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/email-status`);
      const data = await safeJson(r);
      setEmailStatus(data);
    } catch (e) {
      // Graceful degrade — assume Resend mode (current project default) when
      // the backend is unreachable, so we don't show a misleading red banner.
      setEmailStatus({
        provider_mode: 'resend',
        configured_sender: 'avinash@karmakundali.org',
        domain_verified: true,
        domain_status: 'verified (assumed — could not reach backend)',
        sender_domain: 'karmakundali.org',
        error: null,
        _fallback: true,
        _fetch_error: e.message,
      });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/pending-summary`);
      const data = await safeJson(r);
      setPending(data);
    } catch (e) {
      setPending({ error: e.message });
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const fetchBracelets = useCallback(async (status = '') => {
    setBraceletLoading(true);
    try {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await fetch(`${API}/api/admin/bracelet-orders${qs}`);
      const data = await safeJson(r);
      setBracelets(data);
    } catch (e) {
      setBracelets({ error: e.message });
    } finally {
      setBraceletLoading(false);
    }
  }, []);

  const downloadBraceletCsv = () => {
    const qs = braceletStatusFilter ? `?status=${encodeURIComponent(braceletStatusFilter)}` : '';
    window.open(`${API}/api/admin/bracelet-orders/export.csv${qs}`, '_blank');
  };

  const markBraceletShipped = async (orderId) => {
    const tracking = window.prompt('Tracking number (optional):') || '';
    const courier = tracking ? (window.prompt('Courier name (optional, e.g. DTDC):') || '') : '';
    await call(`Mark Shipped (${orderId.slice(0, 8)})`, async () => {
      const r = await fetch(`${API}/api/admin/bracelet-orders/${orderId}/mark-shipped`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tracking_number: tracking, courier }),
      });
      const data = await r.json();
      fetchBracelets(braceletStatusFilter);
      return data;
    });
  };

  const markBraceletDelivered = async (orderId) => {
    if (!window.confirm('Mark this bracelet order as delivered?')) return;
    await call(`Mark Delivered (${orderId.slice(0, 8)})`, async () => {
      const r = await fetch(`${API}/api/admin/bracelet-orders/${orderId}/mark-delivered`, {
        method: 'POST',
      });
      const data = await r.json();
      fetchBracelets(braceletStatusFilter);
      return data;
    });
  };

  // ---- Consultation orders (₹501) ----
  const fetchConsults = useCallback(async (status = '') => {
    setConsultLoading(true);
    try {
      const qs = status ? `?status=${encodeURIComponent(status)}` : '';
      const r = await fetch(`${API}/api/admin/consultation-orders${qs}`);
      const data = await safeJson(r);
      setConsults(data);
    } catch (e) {
      setConsults({ error: e.message });
    } finally {
      setConsultLoading(false);
    }
  }, []);

  const downloadConsultCsv = () => {
    const qs = consultStatusFilter ? `?status=${encodeURIComponent(consultStatusFilter)}` : '';
    window.open(`${API}/api/admin/consultation-orders/export.csv${qs}`, '_blank');
  };

  const markConsultScheduled = async (orderId) => {
    const scheduledAt = window.prompt('Scheduled date/time (ISO format, e.g. 2026-05-08T18:30:00):');
    if (!scheduledAt) return;
    const bookingLink = window.prompt('Booking link / meeting link (optional):') || '';
    await call(`Schedule Consult (${orderId.slice(0, 8)})`, async () => {
      const r = await fetch(`${API}/api/admin/consultation-orders/${orderId}/mark-scheduled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduled_at: scheduledAt, booking_link: bookingLink }),
      });
      const data = await r.json();
      fetchConsults(consultStatusFilter);
      return data;
    });
  };

  const markConsultCompleted = async (orderId) => {
    if (!window.confirm('Mark this consultation as completed?')) return;
    await call(`Complete Consult (${orderId.slice(0, 8)})`, async () => {
      const r = await fetch(`${API}/api/admin/consultation-orders/${orderId}/mark-completed`, {
        method: 'POST',
      });
      const data = await r.json();
      fetchConsults(consultStatusFilter);
      return data;
    });
  };

  const fetchAutoreplies = useCallback(async () => {
    setAutoreplyLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/autoreply/recent?limit=30`);
      const data = await safeJson(r);
      setAutoreplies(data);
    } catch (e) {
      setAutoreplies({ error: e.message });
    } finally {
      setAutoreplyLoading(false);
    }
  }, []);

  const fetchDripStats = useCallback(async () => {
    setEmailLoading(true);
    try {
      const [s, r2] = await Promise.all([
        fetch(`${API}/api/admin/drip/stats`).then(safeJson),
        fetch(`${API}/api/admin/drip/recent-sends?limit=50`).then(safeJson),
      ]);
      setDripStats(s);
      setRecentSends(r2);
    } catch (e) {
      setDripStats({ error: e.message });
    } finally {
      setEmailLoading(false);
    }
  }, []);

  const lookupEmailHistory = () =>
    call(`Email History (${emailHistoryQ})`, async () => {
      if (!emailHistoryQ.trim()) throw new Error('Enter email');
      const r = await fetch(`${API}/api/admin/drip/email-history/${encodeURIComponent(emailHistoryQ.trim().toLowerCase())}`);
      const data = await safeJson(r);
      setEmailHistory(data);
      return { sent_count: data.sent_count, events: data.events?.length || 0 };
    });

  const runAutoreplyNow = () =>
    call('Run AI Autoreply Cycle', async () => {
      const r = await fetch(`${API}/api/admin/autoreply/run-now`, { method: 'POST' });
      const data = await r.json();
      fetchAutoreplies();
      return data;
    });

  useEffect(() => {
    fetchEmailStatus();
    fetchPending();
    fetchConsults();
    fetchBracelets();
    fetchAutoreplies();
    fetchDripStats();
    const t = setInterval(fetchPending, 30000);
    return () => clearInterval(t);
  }, [fetchEmailStatus, fetchPending, fetchConsults, fetchBracelets, fetchAutoreplies, fetchDripStats]);

  return (
    <div className="min-h-screen bg-brand-cream relative">
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-30 pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="bg-gradient-to-br from-brand-maroon via-brand-maroon-dark to-[#3a050d] rounded-3xl shadow-premium border border-brand-gold/40 px-6 py-7 mb-6 text-center overflow-hidden relative">
          <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-25 pointer-events-none" />
          <div className="relative">
            <Sparkles className="w-8 h-8 text-brand-gold-light mx-auto mb-2" />
            <h1 className="font-playfair font-bold text-3xl text-brand-gold-light">Admin Console</h1>
            <p className="text-white/65 text-sm mt-1 tracking-wider">Karma Kundali · Internal Tools</p>
          </div>
        </div>

        {/* Email Provider Status */}
        <Card title="Email Provider Status"
              desc="Karma Kundali abhi Resend (professional email) pe chal raha hai. Domain karmakundali.org verified.">
          {statusLoading && !emailStatus ? (
            <div className="flex items-center gap-2 text-brand-text-mute text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking...
            </div>
          ) : emailStatus ? (
            <div className="space-y-3">
              <div
                data-testid="admin-email-status-banner"
                className={`flex items-start gap-3 p-3 rounded-xl border-2 ${
                  emailStatus.domain_verified === false || emailStatus.error
                    ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'
                }`}
              >
                <CheckCircle2 className="w-6 h-6 text-green-700 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-bold text-green-900">
                    {emailStatus.provider_mode === 'resend'
                      ? `Resend mode active — emails ${emailStatus.configured_sender} se jayenge`
                      : `Gmail-only mode active — emails ${emailStatus.configured_sender || 'karmakundali2@gmail.com'} se jayenge`}
                  </div>
                  <div className="text-xs mt-1 text-brand-text-soft">
                    Provider: <code className="bg-white px-1 rounded">{emailStatus.provider_mode || 'gmail'}</code>
                    {' · '}Sender: <code className="bg-white px-1 rounded">{emailStatus.configured_sender || '—'}</code>
                    {' · '}Status: <code className="bg-white px-1 rounded">{emailStatus.domain_status || '—'}</code>
                  </div>
                  {emailStatus._fallback && (
                    <div className="text-xs mt-2 text-amber-800 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>Note: Backend status endpoint reachable nahi tha — defaults dikha rahe hain.</span>
                    </div>
                  )}
                  {emailStatus.error && !emailStatus._fallback && (
                    <div className="text-xs mt-2 text-red-800 flex items-start gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{emailStatus.error}</span>
                    </div>
                  )}
                </div>
              </div>

              <button
                data-testid="admin-refresh-email-status"
                onClick={fetchEmailStatus}
                disabled={statusLoading}
                className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-4 py-2 rounded-full text-sm transition-all disabled:opacity-50"
              >
                {statusLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Refresh Status
              </button>
            </div>
          ) : (
            <p className="text-brand-text-mute text-sm italic">Status load nahi ho paaya.</p>
          )}
        </Card>

        {/* Pending Orders Summary — live status badges */}
        <Card title="Pending Orders Dashboard"
              desc="Har 30 second me auto-refresh hota hai. Force Resend ke baad yahin se progress dekho.">
          {pendingLoading && !pending ? (
            <div className="flex items-center gap-2 text-brand-text-mute text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : pending && !pending.error ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-pending-counters">
                <Counter label="Delivered" value={pending.delivered} color="green" testid="counter-delivered" />
                <Counter label="In progress" value={pending.in_progress} color="blue" testid="counter-inprogress" />
                <Counter label="Recent (<4h)" value={pending.recent} color="amber" testid="counter-recent" />
                <Counter label="Overdue (>4h)" value={pending.overdue} color="orange" testid="counter-overdue" />
                <Counter label="STUCK (>24h)" value={pending.stuck} color="red" testid="counter-stuck" />
              </div>

              {pending.in_progress_list && pending.in_progress_list.length > 0 && (
                <div>
                  <div className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> In Progress (PDF generating right now)
                  </div>
                  <OrderList rows={pending.in_progress_list} accent="blue" onPick={(id) => setOrderId(id)} />
                </div>
              )}

              {pending.stuck_list && pending.stuck_list.length > 0 && (
                <div>
                  <div className="text-sm font-bold text-red-900 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" /> Stuck / Overdue Orders ({pending.stuck_list.length})
                  </div>
                  <OrderList rows={pending.stuck_list} accent="red" onPick={(id) => setOrderId(id)} />
                </div>
              )}

              <div className="flex flex-wrap gap-3 items-center">
                <button
                  data-testid="admin-force-deliver-all"
                  onClick={() => {
                    if (window.confirm(
                      `Sab paid-but-unsent orders ko ABHI dispatch kare? ` +
                      `4-hour delay bypass hoga, koi stuck lock clear hoga, ` +
                      `har order 3-5 minute mein customer inbox mein ho jayega.`
                    )) {
                      forceDeliverAllNow();
                    }
                  }}
                  disabled={busy === 'Force Deliver All Now'}
                  className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-black px-5 py-2.5 rounded-full text-sm shadow-premium transition-all disabled:opacity-60"
                  title="Immediately dispatch every paid-but-unsent Kundali (bypasses 4-hour delay + clears in-flight lock)"
                >
                  {busy === 'Force Deliver All Now'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Zap className="w-4 h-4" />}
                  Force Deliver All Now
                </button>
                <button
                  data-testid="admin-reconcile-razorpay"
                  onClick={reconcileNow}
                  disabled={busy === 'Reconcile Razorpay Now'}
                  className="inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-light text-brand-maroon-dark font-semibold px-5 py-2.5 rounded-full text-sm transition-all disabled:opacity-60"
                  title="Poll Razorpay for every pending order right now"
                >
                  {busy === 'Reconcile Razorpay Now'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />}
                  Reconcile Razorpay Now
                </button>
                <button
                  data-testid="admin-refresh-pending"
                  onClick={fetchPending}
                  disabled={pendingLoading}
                  className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-4 py-2 rounded-full text-sm transition-all disabled:opacity-50"
                >
                  {pendingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh Status
                </button>
                <button
                  data-testid="admin-redeliver-stuck"
                  onClick={() => {
                    if (window.confirm(
                      `${pending.stuck} stuck orders (>24h) ko dobara dispatch kare? ` +
                      `Delivered orders untouched rahenge.`
                    )) {
                      redeliverStuck();
                    }
                  }}
                  disabled={busy === 'Auto-Resend Stuck (>24h)' || pending.stuck === 0}
                  className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 disabled:bg-gray-400 text-white font-semibold px-5 py-2 rounded-full text-sm transition-all disabled:opacity-60"
                  title={pending.stuck === 0 ? 'No stuck orders right now' : `Auto-resend ${pending.stuck} stuck order(s)`}
                >
                  {busy === 'Auto-Resend Stuck (>24h)'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <AlertTriangle className="w-4 h-4" />}
                  Auto-Resend Stuck (>24h) ({pending.stuck})
                </button>
              </div>
            </div>
          ) : (
            <p className="text-brand-text-mute text-sm italic">Loading failed.</p>
          )}
        </Card>

        {/* Chat Consultation Orders (₹299 primary upsell) */}
        <Card title="One-on-One Consultation Orders (₹299 · 30-min)"
              desc="Customer ne Kundali ke baad Astro Avinash se 30-min consultation book ki hai. Yahan se schedule karein aur personally contact karein.">          <div className="flex flex-wrap gap-3 items-center mb-4" data-testid="consult-admin-controls">
            <select
              data-testid="consult-status-filter"
              value={consultStatusFilter}
              onChange={(e) => { setConsultStatusFilter(e.target.value); fetchConsults(e.target.value); }}
              className="px-3 py-2 rounded-xl bg-white border-2 border-brand-gold/30 text-sm"
            >
              <option value="">All statuses</option>
              <option value="created">Created (link sent, awaiting payment)</option>
              <option value="paid">Paid (ready to schedule)</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
            </select>
            <button
              data-testid="consult-refresh-btn"
              onClick={() => fetchConsults(consultStatusFilter)}
              disabled={consultLoading}
              className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-4 py-2 rounded-full text-sm transition-all disabled:opacity-50"
            >
              {consultLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button
              data-testid="consult-export-csv-btn"
              onClick={downloadConsultCsv}
              className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-4 py-2 rounded-full text-sm shadow-maroon-soft transition-all"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          {consultLoading && !consults ? (
            <div className="flex items-center gap-2 text-brand-text-mute text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : consults && !consults.error ? (
            <div data-testid="consult-orders-list">
              <div className="text-xs text-brand-text-soft mb-3">
                Total: <strong>{consults.count}</strong> consultations
                {consultStatusFilter ? <> (filtered: {consultStatusFilter})</> : null}
              </div>
              {consults.count === 0 ? (
                <p className="text-sm italic text-brand-text-mute">Abhi tak koi consultation booking nahi.</p>
              ) : (
                <div className="border-2 border-brand-gold/20 rounded-xl overflow-hidden bg-white">
                  <div className="max-h-[460px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-brand-cream sticky top-0 border-b-2 border-brand-gold/30">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-bold text-brand-maroon">Order</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Customer</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Question / Topic</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Status</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-gold/10">
                        {consults.items.map((b) => {
                          const c = b.customer || {};
                          return (
                            <tr key={b.id} data-testid={`consult-row-${b.id?.slice(0, 8)}`} className="hover:bg-brand-cream/40">
                              <td className="px-3 py-2 align-top">
                                <div className="font-mono text-[10px] text-brand-maroon">{b.id?.slice(0, 8)}</div>
                                <div className="text-[10px] text-brand-text-mute mt-1">{(b.created_at || '').slice(0, 10)}</div>
                                <div className="text-[10px] text-brand-text-soft">₹{(b.amount_paise || 0)/100}</div>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="font-semibold text-brand-text-dark">{c.full_name}</div>
                                <div className="text-brand-text-soft">{c.email}</div>
                                <a href={`https://wa.me/91${c.mobile}`} target="_blank" rel="noreferrer" className="text-brand-maroon underline">+91 {c.mobile}</a>
                              </td>
                              <td className="px-3 py-2 align-top max-w-[280px] text-brand-text-dark">
                                {c.question ? <em>"{c.question}"</em> : <span className="text-brand-text-mute italic">—</span>}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <ConsultBadge status={b.status} />
                                {b.scheduled_at && (
                                  <div className="text-[10px] text-brand-text-soft mt-1">
                                    {b.scheduled_at.slice(0, 16).replace('T', ' ')}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top whitespace-nowrap">
                                {b.status === 'paid' && (
                                  <button
                                    data-testid={`consult-schedule-${b.id?.slice(0, 8)}`}
                                    onClick={() => markConsultScheduled(b.id)}
                                    className="inline-flex items-center gap-1 bg-brand-gold hover:bg-brand-gold-dark text-brand-maroon-dark font-semibold px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider transition-all"
                                  >
                                    Schedule
                                  </button>
                                )}
                                {b.status === 'scheduled' && (
                                  <button
                                    data-testid={`consult-complete-${b.id?.slice(0, 8)}`}
                                    onClick={() => markConsultCompleted(b.id)}
                                    className="inline-flex items-center gap-1 bg-green-700 hover:bg-green-800 text-white font-semibold px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider transition-all"
                                  >
                                    Completed
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-brand-text-mute">Loading failed.</p>
          )}
        </Card>

        {/* Bracelet Orders Dashboard */}
        <Card title="7-Chakra Bracelet Orders (₹1100 · shipped)"
              desc="Drip email har 7th send par bracelet pitch karta hai. Yahan paid orders ko ship karo aur delivered mark karo.">
          <div className="flex flex-wrap gap-3 items-center mb-4" data-testid="bracelet-admin-controls">
            <select
              data-testid="bracelet-status-filter"
              value={braceletStatusFilter}
              onChange={(e) => { setBraceletStatusFilter(e.target.value); fetchBracelets(e.target.value); }}
              className="px-3 py-2 rounded-xl bg-white border-2 border-brand-gold/30 text-sm"
            >
              <option value="">All statuses</option>
              <option value="created">Created (link sent, awaiting payment)</option>
              <option value="paid">Paid (ready to ship)</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
            </select>
            <button
              data-testid="bracelet-refresh-btn"
              onClick={() => fetchBracelets(braceletStatusFilter)}
              disabled={braceletLoading}
              className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-4 py-2 rounded-full text-sm transition-all disabled:opacity-50"
            >
              {braceletLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
            <button
              data-testid="bracelet-export-csv-btn"
              onClick={downloadBraceletCsv}
              className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-4 py-2 rounded-full text-sm shadow-maroon-soft transition-all"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          {braceletLoading && !bracelets ? (
            <div className="flex items-center gap-2 text-brand-text-mute text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : bracelets && !bracelets.error ? (
            <div data-testid="bracelet-orders-list">
              <div className="text-xs text-brand-text-soft mb-3">
                Total: <strong>{bracelets.count}</strong> orders
                {braceletStatusFilter ? <> (filtered: {braceletStatusFilter})</> : null}
              </div>
              {bracelets.count === 0 ? (
                <p className="text-sm italic text-brand-text-mute">No bracelet orders yet.</p>
              ) : (
                <div className="border-2 border-brand-gold/20 rounded-xl overflow-hidden bg-white">
                  <div className="max-h-[460px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-brand-cream sticky top-0 border-b-2 border-brand-gold/30">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-bold text-brand-maroon">Order</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Customer</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Address</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Status</th>
                          <th className="px-3 py-2 font-bold text-brand-maroon">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-brand-gold/10">
                        {bracelets.items.map((b) => {
                          const sh = b.shipping || {};
                          return (
                            <tr key={b.id} data-testid={`bracelet-row-${b.id?.slice(0, 8)}`} className="hover:bg-brand-cream/40">
                              <td className="px-3 py-2 align-top">
                                <div className="font-mono text-[10px] text-brand-maroon">{b.id?.slice(0, 8)}</div>
                                <div className="text-[10px] text-brand-text-mute mt-1">{(b.created_at || '').slice(0, 10)}</div>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="font-semibold text-brand-text-dark">{sh.full_name}</div>
                                <div className="text-brand-text-soft">{sh.email}</div>
                                <div className="text-brand-text-soft">+91 {sh.mobile}</div>
                              </td>
                              <td className="px-3 py-2 align-top max-w-[280px]">
                                <div className="text-brand-text-dark truncate" title={`${sh.address_line1} ${sh.address_line2}`}>
                                  {sh.address_line1}
                                  {sh.address_line2 ? `, ${sh.address_line2}` : ''}
                                </div>
                                <div className="text-brand-text-soft">{sh.city}, {sh.state} - {sh.pincode}</div>
                                {sh.landmark && <div className="text-brand-text-mute italic">Landmark: {sh.landmark}</div>}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <BraceletBadge status={b.status} />
                                {b.tracking_number && (
                                  <div className="text-[10px] text-brand-text-soft mt-1">
                                    {b.courier}: <code>{b.tracking_number}</code>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top whitespace-nowrap">
                                {b.status === 'paid' && (
                                  <button
                                    data-testid={`bracelet-mark-shipped-${b.id?.slice(0, 8)}`}
                                    onClick={() => markBraceletShipped(b.id)}
                                    className="inline-flex items-center gap-1 bg-brand-gold hover:bg-brand-gold-dark text-brand-maroon-dark font-semibold px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider transition-all"
                                  >
                                    <Truck className="w-3 h-3" /> Ship
                                  </button>
                                )}
                                {b.status === 'shipped' && (
                                  <button
                                    data-testid={`bracelet-mark-delivered-${b.id?.slice(0, 8)}`}
                                    onClick={() => markBraceletDelivered(b.id)}
                                    className="inline-flex items-center gap-1 bg-green-700 hover:bg-green-800 text-white font-semibold px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider transition-all"
                                  >
                                    <PackageCheck className="w-3 h-3" /> Delivered
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-brand-text-mute">Loading failed.</p>
          )}
        </Card>

        {/* AI Autoreply Logs */}
        <Card title="AI Email Auto-Reply Logs"
              desc="Astro Avinash AI har 5 min me Gmail inbox check karta hai aur customers ko reply karta hai. Yahan se audit karo ki AI ne kya reply diya.">
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <button
              data-testid="autoreply-run-now-btn"
              onClick={runAutoreplyNow}
              disabled={busy === 'Run AI Autoreply Cycle'}
              className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-4 py-2 rounded-full text-sm shadow-maroon-soft transition-all disabled:opacity-50"
            >
              {busy === 'Run AI Autoreply Cycle' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              Run AI Reply Cycle Now
            </button>
            <button
              data-testid="autoreply-refresh-btn"
              onClick={fetchAutoreplies}
              disabled={autoreplyLoading}
              className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-4 py-2 rounded-full text-sm transition-all disabled:opacity-50"
            >
              {autoreplyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Refresh
            </button>
          </div>

          {autoreplyLoading && !autoreplies ? (
            <div className="flex items-center gap-2 text-brand-text-mute text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading...
            </div>
          ) : autoreplies && !autoreplies.error ? (
            <div data-testid="autoreply-list">
              <div className="text-xs text-brand-text-soft mb-3">
                Recent: <strong>{autoreplies.count}</strong> auto-replies
              </div>
              {autoreplies.count === 0 ? (
                <p className="text-sm italic text-brand-text-mute">No auto-replies sent yet. Send a test email to <code className="bg-white px-1 rounded">karmakundali2@gmail.com</code> and click "Run AI Reply Cycle Now".</p>
              ) : (
                <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
                  {autoreplies.items.map((a, i) => (
                    <details key={i} className="bg-white border-2 border-brand-gold/20 rounded-xl p-3 group" data-testid={`autoreply-item-${i}`}>
                      <summary className="cursor-pointer text-xs flex flex-wrap items-center gap-2 list-none">
                        <Bot className="w-3.5 h-3.5 text-brand-maroon shrink-0" />
                        <span className="font-semibold text-brand-text-dark truncate flex-1 min-w-0">
                          {a.from_name || a.from || 'Unknown'}
                        </span>
                        <span className="text-brand-text-soft text-[10px] truncate">{a.from}</span>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${a.error ? 'bg-red-100 text-red-900' : 'bg-green-100 text-green-900'}`}>
                          {a.error ? 'failed' : 'replied'}
                        </span>
                        <span className="text-brand-text-mute text-[10px]">{(a.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                      </summary>
                      <div className="mt-3 pt-3 border-t border-brand-gold/20 space-y-2 text-xs">
                        <div>
                          <div className="font-bold text-brand-maroon">Subject:</div>
                          <div className="text-brand-text-dark">{a.subject || '(no subject)'}</div>
                        </div>
                        {a.incoming_excerpt && (
                          <div>
                            <div className="font-bold text-brand-maroon">Customer wrote:</div>
                            <div className="text-brand-text-dark whitespace-pre-wrap bg-brand-cream/40 rounded-lg p-2 max-h-32 overflow-y-auto">{a.incoming_excerpt}</div>
                          </div>
                        )}
                        {a.ai_reply && (
                          <div>
                            <div className="font-bold text-brand-maroon">Astro Avinash replied:</div>
                            <div className="text-brand-text-dark whitespace-pre-wrap bg-amber-50 rounded-lg p-2 max-h-48 overflow-y-auto border border-amber-200">{a.ai_reply}</div>
                          </div>
                        )}
                        {a.error && (
                          <div className="text-red-800 bg-red-50 rounded-lg p-2 border border-red-200">
                            <strong>Error:</strong> {a.error}
                          </div>
                        )}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-brand-text-mute">Loading failed.</p>
          )}
        </Card>

        {/* Run Watchdog */}
        <Card title="Watchdog — Dispatch All Overdue Kundalis"
              desc="हर 5 मिनट automatic चलता है, पर तुरंत trigger करना हो तो दबाओ।">
          <button
            data-testid="admin-run-watchdog"
            onClick={runWatchdog}
            disabled={busy === 'Run Watchdog'}
            className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-5 py-2.5 rounded-full shadow-maroon-soft hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            {busy === 'Run Watchdog' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Run Watchdog Now
          </button>
        </Card>

        {/* EMERGENCY — Redeliver All Paid Orders */}
        <Card title="Emergency — Redeliver Every Paid Order"
              desc="Agar koi customer ko Kundali nahi mili (Resend mis-config / Gmail spam ke karan), to ek click me database ke saare paid orders ko dispatch karo. 'Force' mode pehle se delivered orders ko bhi dobara bhej dega.">
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="admin-redeliver-pending"
              onClick={() => redeliverAllPaid(false)}
              disabled={busy?.startsWith('Redeliver ALL')}
              className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-5 py-2.5 rounded-full shadow-maroon-soft hover:-translate-y-0.5 transition-all disabled:opacity-50"
            >
              {busy === 'Redeliver ALL Paid' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Dispatch Pending Only
            </button>
            <button
              data-testid="admin-redeliver-force-all"
              onClick={() => {
                if (window.confirm('Force = sab paid orders dobara bhejega (already-delivered bhi). Sure?')) {
                  redeliverAllPaid(true);
                }
              }}
              disabled={busy?.startsWith('Redeliver ALL')}
              className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white font-semibold px-5 py-2.5 rounded-full shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50"
            >
              {busy === 'Redeliver ALL Paid (force)' ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
              Force Resend ALL (including delivered)
            </button>
          </div>
        </Card>

        {/* Order ID actions */}
        <Card title="By Order ID"
              desc="किसी एक specific order की state देखें या वही दोबारा भेजें।">
          <input
            data-testid="admin-order-id"
            type="text"
            placeholder="e11abc0b-5bdb-4b3c-80af-d4fce495a32f"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none font-mono text-sm"
          />
          <div className="flex gap-3 mt-3 flex-wrap">
            <button
              data-testid="admin-check-order"
              onClick={checkOrder}
              disabled={busy?.startsWith('Order Status')}
              className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-5 py-2.5 rounded-full transition-all disabled:opacity-50"
            >
              <Search className="w-4 h-4" />
              Check Status
            </button>
            <button
              data-testid="admin-resend-order"
              onClick={resendOrder}
              disabled={busy?.startsWith('Resend')}
              className="inline-flex items-center gap-2 bg-brand-gold hover:bg-brand-gold-dark text-brand-maroon-dark font-semibold px-5 py-2.5 rounded-full shadow-gold-glow hover:-translate-y-0.5 transition-all disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              Force Resend Kundali
            </button>
          </div>
        </Card>

        {/* Email actions */}
        <Card title="By Customer Email"
              desc="किसी customer के सब paid orders दोबारा dispatch करें।">
          <input
            data-testid="admin-email"
            type="email"
            placeholder="customer@example.com"
            value={emailQ}
            onChange={(e) => setEmailQ(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm"
          />
          <button
            data-testid="admin-redeliver-email"
            onClick={redeliverByEmail}
            disabled={busy?.startsWith('Redeliver')}
            className="mt-3 inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-5 py-2.5 rounded-full shadow-maroon-soft hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            <Mail className="w-4 h-4" />
            Redeliver All Paid Orders
          </button>
        </Card>

        {/* Email Tracking Dashboard — drip campaign visibility */}
        <Card title="Email Tracking Dashboard"
              desc="Kaunsa customer ko kitne aur kaunse emails gaye — live view. Har customer ka pura email history dekh sakte ho.">
          {emailLoading && !dripStats ? (
            <div className="flex items-center gap-2 text-brand-text-mute text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading email stats...
            </div>
          ) : dripStats && !dripStats.error ? (
            <div className="space-y-5" data-testid="email-tracking-dashboard">
              {/* Top-line counters */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Counter label="Active customers" value={dripStats.active_enrollments || 0} color="blue" testid="drip-counter-active" />
                <Counter label="Emails sent (total)" value={dripStats.total_emails_sent || 0} color="green" testid="drip-counter-sent" />
                <Counter label="Due right now" value={dripStats.due_for_send_now || 0} color="amber" testid="drip-counter-due" />
                <Counter label="Stopped / opted-out" value={dripStats.stopped_enrollments || 0} color="orange" testid="drip-counter-stopped" />
              </div>

              {/* Domain health */}
              {dripStats.domain_health && (
                <div className={`p-3 rounded-xl border-2 text-sm ${
                  dripStats.domain_health.auto_throttled
                    ? 'bg-red-50 border-red-300'
                    : 'bg-green-50 border-green-300'
                }`} data-testid="drip-domain-health">
                  <div className="flex items-start gap-2">
                    {dripStats.domain_health.auto_throttled
                      ? <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                      : <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />}
                    <div>
                      <div className="font-bold">
                        {dripStats.domain_health.auto_throttled
                          ? '⚠️ Domain AUTO-THROTTLED — complaint rate above safe threshold'
                          : 'Domain healthy'}
                      </div>
                      <div className="text-xs mt-1 text-brand-text-soft">
                        Complaint rate (7d): <strong>{dripStats.domain_health.complaint_rate_pct}%</strong>
                        {' · '}Complaints: {dripStats.domain_health.complaints_last_7d}
                        {' · '}Total events: {dripStats.domain_health.total_events_last_7d}
                        {' · '}Gmail threshold: {dripStats.domain_health.gmail_spam_threshold_pct}%
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Email history lookup */}
              <div>
                <label className="block text-xs font-bold text-brand-text-dark mb-1.5 tracking-wider uppercase">
                  Kisi customer ka pura email history
                </label>
                <div className="flex flex-wrap gap-2">
                  <input
                    data-testid="email-history-input"
                    type="email"
                    placeholder="customer@example.com"
                    value={emailHistoryQ}
                    onChange={(e) => setEmailHistoryQ(e.target.value)}
                    className="flex-1 min-w-[220px] px-4 py-2.5 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm"
                  />
                  <button
                    data-testid="email-history-lookup-btn"
                    onClick={lookupEmailHistory}
                    disabled={busy?.startsWith('Email History')}
                    className="inline-flex items-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark text-brand-gold-light font-semibold px-4 py-2.5 rounded-full shadow-maroon-soft transition-all disabled:opacity-50"
                  >
                    <Search className="w-4 h-4" />
                    Lookup
                  </button>
                  <button
                    data-testid="email-history-refresh-btn"
                    onClick={fetchDripStats}
                    disabled={emailLoading}
                    className="inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-gold text-brand-maroon font-semibold px-4 py-2.5 rounded-full transition-all disabled:opacity-50"
                  >
                    {emailLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Refresh Stats
                  </button>
                </div>
              </div>

              {/* Email history result */}
              {emailHistory && (
                <div className="border-2 border-brand-gold/20 rounded-xl bg-brand-cream/40 p-4" data-testid="email-history-result">
                  <div className="flex flex-wrap items-baseline gap-3 mb-3">
                    <div className="font-bold text-brand-maroon">{emailHistory.email}</div>
                    <div className="text-xs text-brand-text-soft">
                      Sent: <strong>{emailHistory.sent_count}</strong>
                      {' · '}Delivery events: <strong>{emailHistory.events?.length || 0}</strong>
                      {emailHistory.unsubscribed && <span className="ml-2 bg-red-100 text-red-900 border border-red-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Unsubscribed</span>}
                      {emailHistory.enrollment?.stopped_at && (
                        <span className="ml-2 bg-orange-100 text-orange-900 border border-orange-300 px-2 py-0.5 rounded text-[10px] font-bold uppercase">
                          Stopped: {emailHistory.enrollment.stopped_reason}
                        </span>
                      )}
                    </div>
                  </div>
                  {emailHistory.sent?.length > 0 ? (
                    <div className="max-h-72 overflow-y-auto border border-brand-gold/20 rounded-lg bg-white">
                      <table className="w-full text-xs">
                        <thead className="bg-brand-cream sticky top-0 border-b border-brand-gold/20">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-bold text-brand-maroon">#</th>
                            <th className="px-3 py-2 font-bold text-brand-maroon">Sent at</th>
                            <th className="px-3 py-2 font-bold text-brand-maroon">Kind</th>
                            <th className="px-3 py-2 font-bold text-brand-maroon">Subject</th>
                            <th className="px-3 py-2 font-bold text-brand-maroon">From</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-gold/10">
                          {emailHistory.sent.map((s, i) => (
                            <tr key={i} className="hover:bg-brand-cream/40">
                              <td className="px-3 py-2 align-top font-mono text-brand-text-mute">{s.send_number}</td>
                              <td className="px-3 py-2 align-top text-brand-text-soft whitespace-nowrap">{(s.sent_at || '').slice(0, 16).replace('T', ' ')}</td>
                              <td className="px-3 py-2 align-top">
                                <KindBadge kind={s.kind} />
                              </td>
                              <td className="px-3 py-2 align-top max-w-[280px] text-brand-text-dark">
                                <div className="truncate" title={s.subject}>{s.subject}</div>
                              </td>
                              <td className="px-3 py-2 align-top text-brand-text-soft whitespace-nowrap">{s.from_email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm italic text-brand-text-mute">Is customer ko abhi tak koi drip email nahi gaya.</p>
                  )}
                  {emailHistory.events?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs font-bold text-brand-text-dark mb-1.5">Delivery events (Resend webhooks):</div>
                      <div className="flex flex-wrap gap-1.5">
                        {emailHistory.events.slice(0, 20).map((ev, i) => (
                          <span key={i} className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${EVENT_BADGE_MAP[ev.type] || 'bg-gray-100 text-gray-900 border-gray-300'}`}>
                            {ev.type} · {(ev.at || '').slice(5, 16).replace('T', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Recent sends across all customers */}
              {recentSends?.items && (
                <div>
                  <div className="text-xs font-bold text-brand-text-dark mb-2 tracking-wider uppercase">
                    Live feed — recent {recentSends.count} emails sent
                  </div>
                  {recentSends.count === 0 ? (
                    <p className="text-sm italic text-brand-text-mute">Abhi tak koi drip email nahi gaya.</p>
                  ) : (
                    <div className="border-2 border-brand-gold/20 rounded-xl overflow-hidden bg-white">
                      <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-xs" data-testid="drip-recent-sends-table">
                          <thead className="bg-brand-cream sticky top-0 border-b border-brand-gold/20">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-bold text-brand-maroon">Time</th>
                              <th className="px-3 py-2 font-bold text-brand-maroon">To</th>
                              <th className="px-3 py-2 font-bold text-brand-maroon">Kind</th>
                              <th className="px-3 py-2 font-bold text-brand-maroon">Subject</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-brand-gold/10">
                            {recentSends.items.map((s, i) => (
                              <tr key={i} className="hover:bg-brand-cream/40">
                                <td className="px-3 py-2 align-top text-brand-text-soft whitespace-nowrap">{(s.sent_at || '').slice(5, 16).replace('T', ' ')}</td>
                                <td className="px-3 py-2 align-top">
                                  <button
                                    onClick={() => { setEmailHistoryQ(s.email); }}
                                    className="text-brand-maroon underline text-[11px]"
                                    title="Load full history for this email"
                                  >
                                    {s.email}
                                  </button>
                                </td>
                                <td className="px-3 py-2 align-top"><KindBadge kind={s.kind} /></td>
                                <td className="px-3 py-2 align-top max-w-[280px] text-brand-text-dark">
                                  <div className="truncate" title={s.subject}>{s.subject}</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stopped by reason breakdown */}
              {dripStats.stopped_by_reason && Object.keys(dripStats.stopped_by_reason).length > 0 && (
                <div className="text-xs bg-brand-cream/40 rounded-lg border border-brand-gold/20 p-3">
                  <div className="font-bold text-brand-text-dark mb-1.5">Stopped enrollments by reason:</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(dripStats.stopped_by_reason).map(([reason, n]) => (
                      <span key={reason} className="bg-white border border-brand-gold/30 rounded px-2 py-0.5">
                        <strong>{reason}</strong>: {n}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm italic text-brand-text-mute">
              Email stats load nahi ho paaye. {dripStats?.error}
            </p>
          )}
        </Card>

        {/* Diagnostic */}
        <Card title="Diagnostic — Test Email Pipeline"
              desc="Resend production से reachable है या नहीं verify करें।">
          <input
            data-testid="admin-test-email-input"
            type="email"
            placeholder="any-email@example.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm"
          />
          <button
            data-testid="admin-test-email-btn"
            onClick={testEmailSend}
            disabled={busy?.startsWith('Test Email')}
            className="mt-3 inline-flex items-center gap-2 bg-white hover:bg-brand-cream border-2 border-brand-maroon text-brand-maroon font-semibold px-5 py-2.5 rounded-full transition-all disabled:opacity-50"
          >
            <ShieldCheck className="w-4 h-4" />
            Send Test Email
          </button>
        </Card>

        {/* Activity log */}
        <Card title="Activity Log" desc="Recent actions and responses (latest first).">
          {logs.length === 0 ? (
            <p className="text-brand-text-mute italic text-sm">No actions yet — try a button above.</p>
          ) : (
            <div data-testid="admin-log" className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {logs.map((l, i) => (
                <div
                  key={i}
                  className={`text-xs font-mono p-2.5 rounded-lg border break-all ${
                    l.kind === 'ok'
                      ? 'bg-green-50 border-green-200 text-green-900'
                      : l.kind === 'err'
                      ? 'bg-red-50 border-red-200 text-red-900'
                      : 'bg-brand-cream border-brand-gold/30 text-brand-text-dark'
                  }`}
                >
                  <span className="opacity-50 mr-2">{l.t}</span>
                  {l.line}
                </div>
              ))}
            </div>
          )}
        </Card>

        <p className="text-center text-xs text-brand-text-mute mt-8">
          API base: <code className="bg-white px-1.5 py-0.5 rounded">{API}</code>
        </p>
      </div>
    </div>
  );
};

const Card = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border-2 border-brand-gold/30 shadow-sm p-5 sm:p-6 mb-5">
    <h2 className="font-playfair font-bold text-lg text-brand-maroon">{title}</h2>
    <p className="text-brand-text-soft text-sm mb-4">{desc}</p>
    {children}
  </div>
);

const COLOR_MAP = {
  green: 'bg-green-50 border-green-300 text-green-900',
  blue: 'bg-blue-50 border-blue-300 text-blue-900',
  amber: 'bg-amber-50 border-amber-300 text-amber-900',
  orange: 'bg-orange-50 border-orange-300 text-orange-900',
  red: 'bg-red-50 border-red-400 text-red-900',
};

const Counter = ({ label, value, color, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-xl border-2 px-3 py-3 text-center ${COLOR_MAP[color] || COLOR_MAP.amber} ${
      color === 'red' && value > 0 ? 'animate-pulse' : ''
    }`}
  >
    <div className="text-2xl font-bold leading-tight">{value}</div>
    <div className="text-[11px] uppercase tracking-wider mt-1 font-semibold">{label}</div>
  </div>
);

const BRACELET_BADGE_MAP = {
  created: 'bg-amber-100 text-amber-900 border-amber-300',
  paid: 'bg-blue-100 text-blue-900 border-blue-300',
  shipped: 'bg-purple-100 text-purple-900 border-purple-300',
  delivered: 'bg-green-100 text-green-900 border-green-300',
};
const BraceletBadge = ({ status }) => (
  <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${BRACELET_BADGE_MAP[status] || 'bg-gray-100 text-gray-900 border-gray-300'}`}>
    {status || 'unknown'}
  </span>
);

const CONSULT_BADGE_MAP = {
  created: 'bg-amber-100 text-amber-900 border-amber-300',
  paid: 'bg-blue-100 text-blue-900 border-blue-300',
  scheduled: 'bg-indigo-100 text-indigo-900 border-indigo-300',
  completed: 'bg-green-100 text-green-900 border-green-300',
};
const ConsultBadge = ({ status }) => (
  <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${CONSULT_BADGE_MAP[status] || 'bg-gray-100 text-gray-900 border-gray-300'}`}>
    {status || 'unknown'}
  </span>
);

const KIND_BADGE_MAP = {
  sales: 'bg-brand-maroon/10 text-brand-maroon border-brand-maroon/30',
  tip: 'bg-brand-gold/20 text-brand-maroon-dark border-brand-gold',
  bracelet: 'bg-purple-100 text-purple-900 border-purple-300',
};
const KindBadge = ({ kind }) => (
  <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border whitespace-nowrap ${KIND_BADGE_MAP[kind] || 'bg-gray-100 text-gray-900 border-gray-300'}`}>
    {kind || 'unknown'}
  </span>
);

const EVENT_BADGE_MAP = {
  delivered: 'bg-green-100 text-green-900 border-green-300',
  opened: 'bg-blue-100 text-blue-900 border-blue-300',
  complained: 'bg-red-100 text-red-900 border-red-400',
  bounced: 'bg-orange-100 text-orange-900 border-orange-300',
};

const OrderList = ({ rows, accent, onPick }) => {
  const accentColor =
    accent === 'red' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200';
  return (
    <div className={`rounded-xl border-2 ${accentColor} divide-y divide-current/10 max-h-72 overflow-y-auto`}>
      {rows.map((r) => (
        <div key={r.id} className="px-3 py-2 text-xs flex items-center gap-3">
          <button
            onClick={() => onPick && onPick(r.id)}
            className="font-mono text-[11px] bg-white border border-current/20 px-1.5 rounded text-brand-maroon hover:bg-brand-cream"
            title="Copy this Order ID into the Check Status box"
            data-testid={`order-row-${r.id?.slice(0, 8)}`}
          >
            {r.id?.slice(0, 8)}
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate text-brand-text-dark">{r.name}</div>
            <div className="text-brand-text-mute truncate">
              {r.email} · {r.language === 'english' ? 'EN' : 'HI'} · {(r.paid_at || '').slice(0, 10)}
              {r.attempts ? ` · ${r.attempts} attempts` : ''}
            </div>
            {r.last_error && (
              <div className="text-red-700 truncate mt-0.5" title={r.last_error}>
                ✗ {r.last_error}
              </div>
            )}
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold opacity-70">
            {r.stage || (r.dispatching ? 'dispatching' : 'queued')}
          </span>
        </div>
      ))}
    </div>
  );
};

export default AdminPage;
